import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { motion, AnimatePresence, Reorder } from 'framer-motion';
import { 
  Plus, Search, CheckSquare, Clock, AlertCircle,
  Calendar, User, Trash2, X, Check, List, Columns, GripVertical
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

import { API_URL } from '../../config/api';

const statusOptions = [
  { value: 'todo', label: 'À faire', color: 'bg-slate-100 text-slate-600', bgClass: 'bg-slate-50' },
  { value: 'in_progress', label: 'En cours', color: 'bg-blue-100 text-blue-600', bgClass: 'bg-blue-50' },
  { value: 'done', label: 'Terminée', color: 'bg-green-100 text-green-600', bgClass: 'bg-green-50' },
  { value: 'cancelled', label: 'Annulée', color: 'bg-red-100 text-red-600', bgClass: 'bg-red-50' },
];

const priorityOptions = [
  { value: 'low', label: 'Basse', color: 'text-slate-500', dotColor: 'bg-slate-400' },
  { value: 'medium', label: 'Moyenne', color: 'text-amber-500', dotColor: 'bg-amber-400' },
  { value: 'high', label: 'Haute', color: 'text-orange-500', dotColor: 'bg-orange-500' },
  { value: 'urgent', label: 'Urgente', color: 'text-red-500', dotColor: 'bg-red-500' },
];

export default function ClientTasks() {
  const { user } = useOutletContext();
  const [tasks, setTasks] = useState([]);
  const [assistants, setAssistants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('kanban'); // 'list' or 'kanban'
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [presetStatus, setPresetStatus] = useState('');
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    assigned_to: '',
    priority: 'medium',
    due_date: ''
  });

  // Données DÉMO pour le mode sans authentification
  const DEMO_TASKS = [
    { id: 't1', title: 'Préparer devis Villa Rosa', description: 'Finaliser le chiffrage pour la plomberie complète', status: 'todo', priority: 'high', due_date: '2026-04-10', assigned_to: null, created_at: '2026-04-01' },
    { id: 't2', title: 'Relancer M. Dupont', description: 'Appeler pour confirmer le RDV de visite', status: 'todo', priority: 'medium', due_date: '2026-04-08', assigned_to: null, created_at: '2026-04-02' },
    { id: 't3', title: 'Commander matériel chantier Martin', description: 'Robinetterie + raccords', status: 'in_progress', priority: 'high', due_date: '2026-04-07', assigned_to: null, created_at: '2026-04-01' },
    { id: 't4', title: 'Facture entretien copro', description: 'Envoyer facture Les Mimosas', status: 'in_progress', priority: 'low', due_date: '2026-04-15', assigned_to: null, created_at: '2026-03-28' },
    { id: 't5', title: 'Mise à jour planning semaine', description: 'Organiser les interventions de la semaine', status: 'done', priority: 'medium', due_date: '2026-04-05', assigned_to: null, created_at: '2026-03-30' },
    { id: 't6', title: 'Formation nouvel apprenti', description: 'Montrer les bases de la soudure', status: 'todo', priority: 'urgent', due_date: '2026-04-06', assigned_to: null, created_at: '2026-04-03' },
    { id: 't7', title: 'Vérifier stock véhicule', description: 'Inventaire des pièces dans le camion', status: 'done', priority: 'low', due_date: '2026-04-04', assigned_to: null, created_at: '2026-04-01' },
  ];

  const DEMO_ASSISTANTS = [
    { id: 'a1', name: 'Pierre Martin', role: 'Apprenti' },
    { id: 'a2', name: 'Julie Dubois', role: 'Assistante admin' },
  ];

  useEffect(() => {
    fetchTasks();
    fetchAssistants();
  }, []);

  const fetchTasks = async () => {
    const token = localStorage.getItem('client_token');
    
    // MODE DÉMO : Si pas de token, utiliser les données fictives
    if (!token) {
      setTasks(DEMO_TASKS);
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/client/tasks`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setTasks(data);
      }
    } catch (error) {
      console.error('Error fetching tasks:', error);
      // En cas d'erreur, utiliser les données démo
      setTasks(DEMO_TASKS);
    } finally {
      setLoading(false);
    }
  };

  const fetchAssistants = async () => {
    const token = localStorage.getItem('client_token');
    
    // MODE DÉMO
    if (!token) {
      setAssistants(DEMO_ASSISTANTS);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/client/users/${user?.id}/assistants`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setAssistants(data);
      }
    } catch (error) {
      console.error('Error fetching assistants:', error);
      setAssistants(DEMO_ASSISTANTS);
    }
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('client_token');

    try {
      const response = await fetch(`${API_URL}/api/client/tasks`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...newTask,
          status: presetStatus || 'todo',
          due_date: newTask.due_date ? new Date(newTask.due_date).toISOString() : null
        })
      });

      if (response.ok) {
        toast.success('Tâche créée avec succès');
        setShowCreateModal(false);
        setNewTask({ title: '', description: '', assigned_to: '', priority: 'medium', due_date: '' });
        setPresetStatus('');
        fetchTasks();
      } else {
        toast.error('Erreur lors de la création');
      }
    } catch (error) {
      toast.error('Erreur lors de la création');
    }
  };

  const handleUpdateStatus = async (taskId, newStatus) => {
    const token = localStorage.getItem('client_token');

    // Optimistic update
    setTasks(prev => prev.map(t => t.id === taskId ? {...t, status: newStatus} : t));

    try {
      const response = await fetch(`${API_URL}/api/client/tasks/${taskId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        toast.success('Statut mis à jour');
      } else {
        fetchTasks(); // Revert on error
        toast.error('Erreur lors de la mise à jour');
      }
    } catch (error) {
      fetchTasks();
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!confirm('Es-tu sûr de vouloir supprimer cette tâche ?')) return;

    const token = localStorage.getItem('client_token');

    try {
      const response = await fetch(`${API_URL}/api/client/tasks/${taskId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Tâche supprimée');
        setTasks(prev => prev.filter(t => t.id !== taskId));
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleDragEnd = (taskId, newStatus) => {
    handleUpdateStatus(taskId, newStatus);
  };

  const openCreateModal = (status = '') => {
    setPresetStatus(status);
    setShowCreateModal(true);
  };

  const filteredTasks = tasks.filter(task =>
    task.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    task.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getTasksByStatus = (status) => filteredTasks.filter(t => t.status === status);
  const getStatusInfo = (status) => statusOptions.find(s => s.value === status) || statusOptions[0];
  const getPriorityInfo = (priority) => priorityOptions.find(p => p.value === priority) || priorityOptions[1];

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  const isOverdue = (dateString) => {
    if (!dateString) return false;
    return new Date(dateString) < new Date();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="tasks-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-rm-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="tasks-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-rm-text">Mes Tâches</h1>
          <p className="text-rm-text-secondary">Gérez et suivez tes tâches avec ton équipe</p>
        </div>
        <div className="flex items-center gap-3">
          {/* View Toggle */}
          <div className="flex items-center bg-slate-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-md transition-colors ${
                viewMode === 'list' ? 'bg-white shadow-sm text-rm-primary' : 'text-slate-500 hover:text-slate-700'
              }`}
              data-testid="view-list-btn"
            >
              <List className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('kanban')}
              className={`p-2 rounded-md transition-colors ${
                viewMode === 'kanban' ? 'bg-white shadow-sm text-rm-primary' : 'text-slate-500 hover:text-slate-700'
              }`}
              data-testid="view-kanban-btn"
            >
              <Columns className="w-4 h-4" />
            </button>
          </div>
          <Button
            onClick={() => openCreateModal()}
            className="bg-rm-primary hover:bg-rm-primary/90"
            data-testid="create-task-btn"
          >
            <Plus className="w-5 h-5 mr-2" />
            Nouvelle tâche
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Rechercher une tâche..."
          className="w-full pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
          data-testid="search-tasks"
        />
      </div>

      {/* Kanban View */}
      {viewMode === 'kanban' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="kanban-board">
          {statusOptions.map((status) => {
            const statusTasks = getTasksByStatus(status.value);
            return (
              <div 
                key={status.value}
                className={`rounded-xl p-4 ${status.bgClass} min-h-[400px]`}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  const taskId = e.dataTransfer.getData('taskId');
                  if (taskId) handleDragEnd(taskId, status.value);
                }}
              >
                {/* Column Header */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-semibold px-2 py-1 rounded-full ${status.color}`}>
                      {status.label}
                    </span>
                    <span className="text-xs text-slate-500 bg-white/60 px-2 py-0.5 rounded-full">
                      {statusTasks.length}
                    </span>
                  </div>
                  <button
                    onClick={() => openCreateModal(status.value)}
                    className="p-1 hover:bg-white/60 rounded text-slate-500 hover:text-rm-primary"
                    title={`Ajouter dans "${status.label}"`}
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>

                {/* Tasks */}
                <div className="space-y-3">
                  {statusTasks.map((task) => {
                    const priorityInfo = getPriorityInfo(task.priority);
                    const overdue = isOverdue(task.due_date) && status.value !== 'done';

                    return (
                      <motion.div
                        key={task.id}
                        layout
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.9 }}
                        draggable
                        onDragStart={(e) => {
                          e.dataTransfer.setData('taskId', task.id);
                        }}
                        className="bg-white rounded-lg p-3 shadow-sm cursor-grab active:cursor-grabbing hover:shadow-md transition-shadow"
                        data-testid={`task-card-${task.id}`}
                      >
                        <div className="flex items-start gap-2">
                          <GripVertical className="w-4 h-4 text-slate-300 flex-shrink-0 mt-0.5" />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <h4 className="font-medium text-sm text-rm-text line-clamp-2">
                                {task.title}
                              </h4>
                              <button
                                onClick={() => handleDeleteTask(task.id)}
                                className="p-1 hover:bg-red-100 rounded text-slate-400 hover:text-red-500 flex-shrink-0"
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </div>
                            
                            {task.description && (
                              <p className="text-xs text-rm-text-secondary mt-1 line-clamp-2">
                                {task.description}
                              </p>
                            )}

                            <div className="flex flex-wrap items-center gap-2 mt-2">
                              <span className={`w-2 h-2 rounded-full ${priorityInfo.dotColor}`} title={priorityInfo.label} />
                              
                              {task.assigned_to_name && (
                                <span className="text-xs text-slate-500 flex items-center gap-1">
                                  <User className="w-3 h-3" />
                                  {task.assigned_to_name.split(' ')[0]}
                                </span>
                              )}
                              
                              {task.due_date && (
                                <span className={`text-xs flex items-center gap-1 ${
                                  overdue ? 'text-red-500' : 'text-slate-500'
                                }`}>
                                  <Calendar className="w-3 h-3" />
                                  {formatDate(task.due_date)}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                  
                  {statusTasks.length === 0 && (
                    <div className="text-center py-8 text-slate-400 text-sm">
                      Aucune tâche
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-100" data-testid="task-list">
          {filteredTasks.length === 0 ? (
            <div className="text-center py-12">
              <CheckSquare className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-rm-text-secondary">Aucune tâche trouvée</p>
              <Button
                onClick={() => openCreateModal()}
                className="mt-4 bg-rm-primary"
              >
                <Plus className="w-4 h-4 mr-2" />
                Créer une tâche
              </Button>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {filteredTasks.map((task) => {
                const statusInfo = getStatusInfo(task.status);
                const priorityInfo = getPriorityInfo(task.priority);
                const overdue = isOverdue(task.due_date) && task.status !== 'done';

                return (
                  <motion.div
                    key={task.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="p-4 hover:bg-slate-50 transition-colors"
                    data-testid={`task-row-${task.id}`}
                  >
                    <div className="flex items-start gap-4">
                      {/* Checkbox */}
                      <button
                        onClick={() => handleUpdateStatus(task.id, task.status === 'done' ? 'todo' : 'done')}
                        className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors ${
                          task.status === 'done'
                            ? 'bg-green-500 border-green-500 text-white'
                            : 'border-slate-300 hover:border-rm-primary'
                        }`}
                      >
                        {task.status === 'done' && <Check className="w-4 h-4" />}
                      </button>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <h3 className={`font-medium ${
                              task.status === 'done' ? 'text-slate-400 line-through' : 'text-rm-text'
                            }`}>
                              {task.title}
                            </h3>
                            {task.description && (
                              <p className="text-sm text-rm-text-secondary mt-1 line-clamp-2">
                                {task.description}
                              </p>
                            )}
                          </div>
                          <button
                            onClick={() => handleDeleteTask(task.id)}
                            className="p-1 hover:bg-red-100 rounded text-slate-400 hover:text-red-500"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>

                        {/* Meta */}
                        <div className="flex flex-wrap items-center gap-3 mt-3">
                          <select
                            value={task.status}
                            onChange={(e) => handleUpdateStatus(task.id, e.target.value)}
                            className={`text-xs px-2 py-1 rounded-full border-0 cursor-pointer ${statusInfo.color}`}
                          >
                            {statusOptions.map(s => (
                              <option key={s.value} value={s.value}>{s.label}</option>
                            ))}
                          </select>
                          <span className={`text-xs ${priorityInfo.color} flex items-center gap-1`}>
                            <AlertCircle className="w-3 h-3" />
                            {priorityInfo.label}
                          </span>
                          {task.assigned_to_name && (
                            <span className="text-xs text-rm-text-secondary flex items-center gap-1">
                              <User className="w-3 h-3" />
                              {task.assigned_to_name}
                            </span>
                          )}
                          {task.due_date && (
                            <span className={`text-xs flex items-center gap-1 ${
                              overdue ? 'text-red-500' : 'text-rm-text-secondary'
                            }`}>
                              <Calendar className="w-3 h-3" />
                              {formatDate(task.due_date)}
                              {overdue && <span className="text-red-500">(en retard)</span>}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Create Task Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowCreateModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl p-6 w-full max-w-lg shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-rm-text">Nouvelle tâche</h2>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="p-2 hover:bg-slate-100 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleCreateTask} className="space-y-4" data-testid="create-task-form">
                <div>
                  <label className="block text-sm font-medium text-rm-text mb-1">
                    Titre *
                  </label>
                  <input
                    type="text"
                    required
                    value={newTask.title}
                    onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                    placeholder="Ex: Répondre aux emails clients"
                    data-testid="task-title-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-rm-text mb-1">
                    Description
                  </label>
                  <textarea
                    value={newTask.description}
                    onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                    rows={3}
                    placeholder="Détails de la tâche..."
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-rm-text mb-1">
                      Assigner à
                    </label>
                    <select
                      value={newTask.assigned_to}
                      onChange={(e) => setNewTask({ ...newTask, assigned_to: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                    >
                      <option value="">Non assignée</option>
                      {assistants.map(assistant => (
                        <option key={assistant.id} value={assistant.id}>
                          {assistant.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-rm-text mb-1">
                      Priorité
                    </label>
                    <select
                      value={newTask.priority}
                      onChange={(e) => setNewTask({ ...newTask, priority: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                    >
                      {priorityOptions.map(priority => (
                        <option key={priority.value} value={priority.value}>
                          {priority.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-rm-text mb-1">
                    Date d'échéance
                  </label>
                  <input
                    type="date"
                    value={newTask.due_date}
                    onChange={(e) => setNewTask({ ...newTask, due_date: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                  />
                </div>

                {presetStatus && (
                  <p className="text-sm text-rm-text-secondary">
                    Cette tâche sera créée dans la colonne "{getStatusInfo(presetStatus).label}"
                  </p>
                )}

                <div className="flex justify-end gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowCreateModal(false)}
                  >
                    Annuler
                  </Button>
                  <Button type="submit" className="bg-rm-primary" data-testid="submit-task-btn">
                    Créer la tâche
                  </Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
